import { Fragment, ReactNode, useRef } from "react";
import { useEditorState } from "@tiptap/react";
import { useRichTextEditorContext } from "./RichTextEditorContext";
import { UNICODE_WHITESPACE_REGEX } from "./extensions/MentionExtension";
import { Toolbar } from "../../../../components/Toolbar";
import { Divider } from "../../../../components/Divider";
import { Menu } from "../../../../components/Menu";

import AlternateEmail from "@servicetitan/hammer-icon/mdi/round/alternate_email.svg";
import FormatBold from "@servicetitan/hammer-icon/mdi/round/format_bold.svg";
import FormatItalic from "@servicetitan/hammer-icon/mdi/round/format_italic.svg";
import FormatUnderlined from "@servicetitan/hammer-icon/mdi/round/format_underlined.svg";
import Code from "@servicetitan/hammer-icon/mdi/round/code.svg";
import FormatQuote from "@servicetitan/hammer-icon/mdi/round/format_quote.svg";
import FormatAlignLeft from "@servicetitan/hammer-icon/mdi/round/format_align_left.svg";
import FormatAlignCenter from "@servicetitan/hammer-icon/mdi/round/format_align_center.svg";
import FormatAlignRight from "@servicetitan/hammer-icon/mdi/round/format_align_right.svg";
import FormatAlignJustify from "@servicetitan/hammer-icon/mdi/round/format_align_justify.svg";
import FormatIndentIncrease from "@servicetitan/hammer-icon/mdi/round/format_indent_increase.svg";
import FormatIndentDecrease from "@servicetitan/hammer-icon/mdi/round/format_indent_decrease.svg";
import FormatListBulleted from "@servicetitan/hammer-icon/mdi/round/format_list_bulleted.svg";
import FormatListNumbered from "@servicetitan/hammer-icon/mdi/round/format_list_numbered.svg";
import Checklist from "@servicetitan/hammer-icon/mdi/round/checklist.svg";
import InsertLink from "@servicetitan/hammer-icon/mdi/round/insert_link.svg";
import Image from "@servicetitan/hammer-icon/mdi/round/image.svg";
import SmartDisplay from "@servicetitan/hammer-icon/mdi/round/smart_display.svg";
import Add from "@servicetitan/hammer-icon/mdi/round/add.svg";
import IconStrikethroughS from "@servicetitan/hammer-icon/mdi/round/strikethrough_s.svg";

import styles from "./RichTextEditorToolbar.module.scss";

type ToolbarSection = { key: string; content: ReactNode };

type RichTextEditorToolbarInnerProps = {
  sections: ToolbarSection[];
  disabled: boolean;
  allowMedia: boolean;
  allowedMimeTypes: `image/${string}`[] | undefined;
  fileInputRef: React.RefObject<HTMLInputElement>;
  onFileInputChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
};

const RichTextEditorToolbarInner = ({
  sections,
  disabled,
  allowMedia,
  allowedMimeTypes,
  fileInputRef,
  onFileInputChange,
}: RichTextEditorToolbarInnerProps) => {
  return (
    <div className={styles["toolbar-wrapper"]}>
      <Toolbar
        associatedContent="Text formatting"
        overflow="wrap"
        aria-disabled={disabled}
      >
        {sections.flatMap(({ key, content }, i) =>
          i === 0
            ? [<Fragment key={key}>{content}</Fragment>]
            : [
                <Divider
                  key={`divider-${key}`}
                  vertical
                  flexShrink={0}
                  alignSelf="stretch"
                  aria-hidden
                />,
                <Fragment key={key}>{content}</Fragment>,
              ],
        )}

        {allowMedia && (
          <input
            ref={fileInputRef}
            type="file"
            accept={allowedMimeTypes?.join(",")}
            multiple
            style={{ display: "none" }}
            onChange={onFileInputChange}
          />
        )}
      </Toolbar>
    </div>
  );
};

export const RichTextEditorToolbar = () => {
  const {
    editor,
    disabled,
    allowTextFormat,
    allowTextAlignment,
    allowLists,
    allowChecklist,
    allowCode,
    allowBlockquote,
    allowLink,
    allowMedia,
    allowMention,
    onImageUpload,
    allowedMimeTypes,
    openLinkPopover,
    openYoutubePopover,
  } = useRichTextEditorContext();

  const handleOpenLinkFromToolbar = () => {
    const { from, to, empty } = editor.state.selection;
    const displayText = empty ? "" : editor.state.doc.textBetween(from, to);
    const fromCoords = editor.view.coordsAtPos(from);
    const toCoords = editor.view.coordsAtPos(to);
    openLinkPopover({
      href: editor.getAttributes("link").href ?? "",
      displayText,
      anchorRect: new DOMRect(
        fromCoords.left,
        fromCoords.top,
        toCoords.right - fromCoords.left,
        toCoords.bottom - fromCoords.top,
      ),
    });
  };

  const handleOpenYoutubeFromToolbar = () => {
    const { from } = editor.state.selection;
    const coords = editor.view.coordsAtPos(from);
    openYoutubePopover(new DOMRect(coords.left, coords.bottom, 0, 0));
  };

  const handleImageInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files ?? []);
    if (files.length > 0) {
      onImageUpload?.(files, (srcs) => {
        editor
          .chain()
          .focus()
          .insertContent(
            srcs.flatMap((src, i) => [
              { type: "image", attrs: { src } },
              ...(i < srcs.length - 1 ? [{ type: "paragraph" }] : []),
            ]),
          )
          .run();
      });
    }
    e.target.value = "";
  };

  const handleInsertMention = () => {
    const { from } = editor.state.selection;
    const charBefore = editor.state.doc.textBetween(
      Math.max(0, from - 1),
      from,
    );
    const needsSpace =
      charBefore !== "" && !UNICODE_WHITESPACE_REGEX.test(charBefore);
    editor
      .chain()
      .focus()
      .insertContent(needsSpace ? " @" : "@")
      .setTextSelection(from + (needsSpace ? 2 : 1))
      .run();
  };

  const fileInputRef = useRef<HTMLInputElement>(null);

  const editorState = useEditorState({
    editor: editor,
    selector: (ctx) => {
      const headingLevel = [1, 2, 3].find((l) =>
        ctx.editor.isActive("heading", { level: l }),
      );
      return {
        textType: headingLevel ? `h${headingLevel}` : "p",
        fontSize:
          (ctx.editor.getAttributes("textStyle").fontSize as string) ?? "",
        isBold: ctx.editor.isActive("bold"),
        isItalic: ctx.editor.isActive("italic"),
        isUnderline: ctx.editor.isActive("underline"),
        isStrike: ctx.editor.isActive("strike"),
        isCode: ctx.editor.isActive("code"),
        isBlockquote: ctx.editor.isActive("blockquote"),
        isAlignLeft: ctx.editor.isActive({ textAlign: "left" }),
        isAlignCenter: ctx.editor.isActive({ textAlign: "center" }),
        isAlignRight: ctx.editor.isActive({ textAlign: "right" }),
        isAlignJustify: ctx.editor.isActive({ textAlign: "justify" }),
        isBulletList: ctx.editor.isActive("bulletList"),
        isOrderedList: ctx.editor.isActive("orderedList"),
        isTaskList: ctx.editor.isActive("taskList"),
        isLink: ctx.editor.isActive("link"),
      };
    },
  });

  const sections: { key: string; content: ReactNode }[] = [
    allowTextFormat && {
      key: "text-format",
      content: (
        <>
          <Toolbar.Select
            accessibleLabel="Text type"
            disabled={disabled}
            selected={editorState.textType}
            items={[
              { id: "p", label: "Normal Text" },
              { id: "h1", label: "Heading 1" },
              { id: "h2", label: "Heading 2" },
              { id: "h3", label: "Heading 3" },
            ]}
            onChange={(id: string) => {
              if (id === "p") {
                editor.chain().focus().setParagraph().run();
              } else {
                editor
                  .chain()
                  .focus()
                  .toggleHeading({ level: Number(id[1]) as 1 | 2 | 3 })
                  .run();
              }
            }}
          />
          <Toolbar.Select
            accessibleLabel="Font size"
            disabled={disabled}
            selected={editorState.fontSize || "16px"}
            items={[
              "12px",
              "14px",
              "15px",
              "16px",
              "18px",
              "20px",
              "24px",
              "28px",
              "32px",
              "36px",
            ].map((size) => ({ id: size, label: size }))}
            onChange={(id: string) => {
              editor.chain().focus().setFontSize(id).run();
            }}
          />
          <Toolbar.ButtonToggle
            icon={FormatBold}
            aria-label="Bold"
            checked={editorState.isBold}
            disabled={disabled}
            className={styles["btn-toggle"]}
            onClick={() => editor.chain().focus().toggleBold().run()}
          />
          <Toolbar.ButtonToggle
            icon={FormatItalic}
            aria-label="Italic"
            checked={editorState.isItalic}
            disabled={disabled}
            className={styles["btn-toggle"]}
            onClick={() => editor.chain().focus().toggleItalic().run()}
          />
          <Toolbar.ButtonToggle
            icon={FormatUnderlined}
            aria-label="Underline"
            checked={editorState.isUnderline}
            disabled={disabled}
            className={styles["btn-toggle"]}
            onClick={() => editor.chain().focus().toggleUnderline().run()}
          />
          <Toolbar.ButtonToggle
            icon={IconStrikethroughS}
            aria-label="Strikethrough"
            checked={editorState.isStrike}
            disabled={disabled}
            className={styles["btn-toggle"]}
            onClick={() => editor.chain().focus().toggleStrike().run()}
          />
        </>
      ),
    },

    allowCode && {
      key: "code",
      content: (
        <Toolbar.ButtonToggle
          icon={Code}
          aria-label="Inline code"
          checked={editorState.isCode}
          disabled={disabled}
          className={styles["btn-toggle"]}
          onClick={() => editor.chain().focus().toggleCode().run()}
        />
      ),
    },

    allowBlockquote && {
      key: "blockquote",
      content: (
        <Toolbar.ButtonToggle
          icon={FormatQuote}
          aria-label="Blockquote"
          checked={editorState.isBlockquote}
          disabled={disabled}
          className={styles["btn-toggle"]}
          onClick={() => editor.chain().focus().toggleBlockquote().run()}
        />
      ),
    },

    allowTextAlignment && {
      key: "text-alignment",
      content: (
        <>
          {(
            [
              {
                align: "left",
                icon: FormatAlignLeft,
                label: "Align left",
                isActive: editorState.isAlignLeft,
              },
              {
                align: "center",
                icon: FormatAlignCenter,
                label: "Align center",
                isActive: editorState.isAlignCenter,
              },
              {
                align: "right",
                icon: FormatAlignRight,
                label: "Align right",
                isActive: editorState.isAlignRight,
              },
              {
                align: "justify",
                icon: FormatAlignJustify,
                label: "Justify",
                isActive: editorState.isAlignJustify,
              },
            ] as const
          ).map(({ align, icon, label, isActive }) => (
            <Toolbar.ButtonToggle
              key={align}
              icon={icon}
              aria-label={label}
              checked={isActive}
              disabled={disabled}
              className={styles["btn-toggle"]}
              onClick={() => editor.chain().focus().setTextAlign(align).run()}
            />
          ))}
        </>
      ),
    },

    allowLists && {
      key: "lists",
      content: (
        <>
          <Toolbar.ButtonToggle
            icon={FormatListBulleted}
            aria-label="Bullet list"
            checked={editorState.isBulletList}
            disabled={disabled}
            className={styles["btn-toggle"]}
            onClick={() => editor.chain().focus().toggleBulletList().run()}
          />
          <Toolbar.ButtonToggle
            icon={FormatListNumbered}
            aria-label="Ordered list"
            checked={editorState.isOrderedList}
            disabled={disabled}
            className={styles["btn-toggle"]}
            onClick={() => editor.chain().focus().toggleOrderedList().run()}
          />
          <Toolbar.Button
            icon={FormatIndentDecrease}
            aria-label="Decrease indent"
            disabled={disabled}
            onClick={() =>
              editor.chain().focus().liftListItem("listItem").run()
            }
          />
          <Toolbar.Button
            icon={FormatIndentIncrease}
            aria-label="Increase indent"
            disabled={disabled}
            onClick={() =>
              editor.chain().focus().sinkListItem("listItem").run()
            }
          />
        </>
      ),
    },

    allowChecklist && {
      key: "checklist",
      content: (
        <Toolbar.ButtonToggle
          icon={Checklist}
          aria-label="Checklist"
          checked={editorState.isTaskList}
          disabled={disabled}
          className={styles["btn-toggle"]}
          onClick={() => editor.chain().focus().toggleTaskList().run()}
        />
      ),
    },

    allowLink && {
      key: "link",
      content: (
        <Toolbar.ButtonToggle
          icon={InsertLink}
          aria-label="Insert link"
          checked={editorState.isLink}
          disabled={disabled}
          className={styles["btn-toggle"]}
          onClick={handleOpenLinkFromToolbar}
        />
      ),
    },

    allowMedia && {
      key: "media",
      content: (
        <Menu
          placement="bottom-start"
          trigger={({ onClick, ...triggerProps }) => (
            <Toolbar.Button
              {...triggerProps}
              icon={Add}
              aria-label="Insert media"
              disabled={disabled}
              onClick={onClick}
            />
          )}
        >
          <Menu.Item
            icon={Image}
            label="Image"
            onClick={() => fileInputRef.current?.click()}
          />
          <Menu.Item
            icon={SmartDisplay}
            label="YouTube"
            onClick={handleOpenYoutubeFromToolbar}
          />
        </Menu>
      ),
    },

    allowMention && {
      key: "mention",
      content: (
        <Toolbar.Button
          icon={AlternateEmail}
          aria-label="Insert mention"
          disabled={disabled}
          onClick={handleInsertMention}
        />
      ),
    },
  ].filter(Boolean) as ToolbarSection[];

  if (sections.length === 0) return null;

  return (
    <RichTextEditorToolbarInner
      sections={sections}
      disabled={disabled}
      allowMedia={allowMedia}
      allowedMimeTypes={allowedMimeTypes}
      fileInputRef={fileInputRef}
      onFileInputChange={handleImageInputChange}
    />
  );
};
